import { markAuthoredBlankLines } from '@/utils/authored_blank_lines';
import {
  KABBALAH_ENTRY_ATTR,
  KABBALAH_ENTRY_VALUE,
  KABBALAH_PHOTO_IMAGE_ATTR,
} from '@/utils/kabbalah_design_templates';

export interface KabbalahRenderEntry_N {
  /** The two halves joined — what `{fullName}` renders. */
  fullName: string;
  /** The two halves, so a design can print either on its own line. */
  firstName: string;
  lastName: string;
  gabbai: string;
  /** The kabbalah itself, in the submitter's own words. */
  kabbalah: string;
  /** The accepted length, already worded by `kabbalahDurationLabel`. */
  duration: string;
  image?: string | null;
  /**
   * City and Beis Medrash were dropped from the form. They are still carried —
   * always empty — so a design saved while they existed collapses those lines
   * away, exactly as an unfilled gabbai does, instead of printing a literal
   * `{city}` on a wall.
   */
  city?: string;
  beisMedrash?: string;
}

export interface RenderKabbalahDesignArgs_N {
  // The design authored in the editor.
  html: string;
  // One resolved entry per block the design carries.
  entries: KabbalahRenderEntry_N[];
  // Falls back for the photo placeholder when no entry carries a picture.
  leadImage?: string | null;
  // Production cards do not have editor sample content to pad an incomplete
  // final card. Remove authored entry blocks without a matching real record.
  removeUnfilledEntries?: boolean;
}

// Swap {token} for the entry's value across a subtree's text nodes.
//
// Done on text nodes rather than by string-replacing the HTML so a value can
// never introduce markup, and a token sitting inside an attribute (a URL, say)
// is left alone.
const substituteVariables = (
  root: Element,
  entry: KabbalahRenderEntry_N,
  shouldSubstituteNode: (node: Text) => boolean = () => true
): void => {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];

  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    if (
      node.nodeValue &&
      node.nodeValue.indexOf('{') !== -1 &&
      shouldSubstituteNode(node)
    ) {
      targets.push(node);
    }
  }

  targets.forEach((node) => {
    node.nodeValue = (node.nodeValue as string).replace(
      /\{(\w+)\}/g,
      (match, key) =>
        Object.prototype.hasOwnProperty.call(entry, key)
          ? String((entry as any)[key] ?? '')
          : match
    );
  });
};

const isInsideRecordEntryBlock = (node: Text): boolean => {
  const parent = node.parentElement;
  return Boolean(
    parent?.closest(`[${KABBALAH_ENTRY_ATTR}="${KABBALAH_ENTRY_VALUE}"]`)
  );
};

// The record fields a {token} can name. Kept in step with `substituteVariables`
// above — it replaces `{key}` only when the entry carries that key — so one set
// decides both what gets filled in and what counts as drawing a record.
const RECORD_VARIABLE_KEYS = new Set([
  'fullName', 'firstName', 'lastName', 'gabbai', 'kabbalah', 'duration', 'image',
  // Retired tokens. Kept in the set so a block whose only token is one of them
  // still counts as a record block — dropping it would shrink the card's
  // capacity and silently swallow records the design was built to hold.
  'city', 'beisMedrash',
]);

// Whether a block would show anything of the record handed to it.
//
// An entry block the author emptied — every token deleted, or the whole layout
// lifted out to the card around it — renders identically whichever record it is
// given, so it is not a place a person can appear. The husk still sits in the
// saved HTML, which is why the count below has to ask rather than assume.
const entryBlockDrawsRecord = (block: Element): boolean => {
  const walker = block.ownerDocument.createTreeWalker(block, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const value = (walker.currentNode as Text).nodeValue;
    if (!value || value.indexOf('{') === -1) continue;

    const tokens = value.match(/\{(\w+)\}/g) || [];
    if (tokens.some((token) => RECORD_VARIABLE_KEYS.has(token.slice(1, -1)))) {
      return true;
    }
  }

  return false;
};

// The blocks of a design that each draw one record, in document order.
const recordEntryBlocks = (root: Element): Element[] =>
  Array.from(
    root.querySelectorAll(`[${KABBALAH_ENTRY_ATTR}="${KABBALAH_ENTRY_VALUE}"]`)
  ).filter(entryBlockDrawsRecord);

// How many records ONE card draws.
//
// It is the design's own repeated blocks that decide this, not the template's
// nominal entry count: an author who deletes two of the three blocks has built a
// one-person card, and grouping records three at a time would silently swallow
// two of every three. A design with no blocks left fills its variables from the
// first record, which is a capacity of one all the same.
//
// An emptied block counts the same as a deleted one. A design whose names were
// lifted out to the card, leaving the blocks behind as bare <div>s, draws
// exactly one person however many husks remain — counting them would hand the
// card records that appear nowhere on it, and make a board of six look full
// enough to need no second page.
export const countKabbalahEntryBlocks_N = (html: string): number => {
  if (!html || !html.trim()) return 0;

  const parsed = new DOMParser().parseFromString(
    `<div id="kb-root">${html}</div>`,
    'text/html'
  );
  const root = parsed.getElementById('kb-root');
  if (!root) return 0;

  return recordEntryBlocks(root).length;
};

export const getKabbalahCardCapacity_N = (html: string): number =>
  Math.max(1, countKabbalahEntryBlocks_N(html));

// Render an authored design against a list of records.
//
// The design holds the WHOLE layout — one <div data-kb-layout="entry"> per row
// the author laid out — so what the editor shows is what renders. The Nth block
// is filled from the Nth record; blocks past the end of the list keep their
// placeholder text so the layout never collapses.
export const renderKabbalahDesignHtml_N = ({
  html,
  entries,
  leadImage = null,
  removeUnfilledEntries = false,
}: RenderKabbalahDesignArgs_N): string => {
  if (!html || !html.trim()) return '';

  const parsed = new DOMParser().parseFromString(
    `<div id="kb-root">${html}</div>`,
    'text/html'
  );
  const root = parsed.getElementById('kb-root');
  if (!root) return '';

  // Before any substitution: a blank line the author left and a field whose
  // {token} the record has no value for both end up as an empty block, and only
  // the first of them is a line the card should draw.
  markAuthoredBlankLines(root);

  // The photo placeholder follows the records only while it is still a stand-in.
  // Once an upload replaces it the attribute is gone and the author's own image
  // is left exactly as they set it.
  const photo = entries.find((item) => item && item.image)?.image || leadImage;
  if (photo) {
    root
      .querySelectorAll(`img[${KABBALAH_PHOTO_IMAGE_ATTR}="true"]`)
      .forEach((img) => img.setAttribute('src', photo));
  }

  // Only the blocks that draw a record take one, so the Nth record lands in the
  // Nth block a viewer can actually see it in — the same blocks
  // `countKabbalahEntryBlocks_N` sized the card by. Emptied blocks are left
  // exactly as authored: they hold no record, so there is nothing to remove them
  // for either.
  const blocks = recordEntryBlocks(root);

  if (blocks.length) {
    blocks.forEach((block, index) => {
      const entry = entries[index];
      // A block with no record behind it keeps its {tokens} — that is what makes
      // an unfilled row read as a placeholder rather than as blank space.
      if (entry) {
        substituteVariables(block, entry);
      } else if (removeUnfilledEntries) {
        block.remove();
      }
    });
    // Custom designs often add a heading or footer-like field around the
    // repeated record blocks. Resolve those card-level variables from the lead
    // record without disturbing the per-record substitutions above.
    if (entries[0]) {
      substituteVariables(
        root,
        entries[0],
        (node) => !isInsideRecordEntryBlock(node)
      );
    }
  } else if (entries[0]) {
    // A design the author stripped of entry blocks still gets its variables
    // filled, from the first record.
    substituteVariables(root, entries[0]);
  }

  return root.innerHTML;
};
